'use client';

import React, { useState, useRef } from 'react';
import { GlobalButton } from './GlobalButton';
import GlobalSidebar from './GlobalSidebar';
import { GlobalCheckbox } from './GlobalCheckbox';
import { GlobalInput } from './GlobalInput';
import { useToast } from '../context/ToastProvider';
import { useTheme } from '../context/ThemeProvider';
import { useLocale } from '../context/DynamicLocaleProvider';
import { useUser } from '../context/UserContext';
import { useBatchProgress } from '../context/BatchProgressContext';
import { getStorage, ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import LocalLLMTrust from './LocalLLMTrust';
import styles from './EntityGenerationSidebar.module.css';
import type { RecordType } from '../types';
import { fileIconFromFileName } from '../utils/fileTypeGraphics';

interface AddDocumentSidebarProps {
  isOpen: boolean;
  onClose: () => void;
  projectId: string;
  onSuccess?: () => void;
  defaultEntityTypes?: RecordType[]; // Optional: pre-select entity types
}

export default function AddDocumentSidebar({
  isOpen,
  onClose,
  projectId,
  onSuccess,
  defaultEntityTypes
}: AddDocumentSidebarProps) {
  const { t } = useLocale();
  const showToast = useToast();
  const { theme } = useTheme();
  const { user } = useUser();
  const { startBatch, endBatch } = useBatchProgress();
  const isDarkMode = theme === 'dark';
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const [documentName, setDocumentName] = useState('');
  const [enableEmbedding, setEnableEmbedding] = useState(true);
  const [extractEntities, setExtractEntities] = useState(false);
  const [selectedEntityTypes, setSelectedEntityTypes] = useState<RecordType[]>(defaultEntityTypes || ['URS']);
  const [version, setVersion] = useState<string>('1.0');
  const [isLoading, setIsLoading] = useState(false);

  // Available entity types
  const availableEntityTypes: RecordType[] = ['URS', 'FS', 'TEST', 'RISK', 'SOP'];

  // Get localized entity type names
  const getEntityTypeName = (type: RecordType): string => {
    const nameMap: Record<RecordType, string> = {
      'URS': t('project_page_tab_urs') || 'Requirements',
      'FS': t('project_page_tab_fs') || 'Specifications',
      'RISK': t('project_page_tab_ra') || 'Risks',
      'TEST': t('project_page_tab_tests') || 'Tests',
      'SOP': t('project_page_tab_sops') || 'SOPs',
    };
    return nameMap[type] || type;
  };

  const handleEntityTypeToggle = (type: RecordType) => {
    setSelectedEntityTypes(prev => {
      if (prev.includes(type)) {
        // Don't allow deselecting all types if extractEntities is enabled
        if (prev.length === 1 && extractEntities) {
          showToast(
            t('add_document_at_least_one_entity_type') || 'Please select at least one entity type',
            'warning'
          );
          return prev;
        }
        return prev.filter(t => t !== type);
      } else {
        return [...prev, type];
      }
    });
  };

  // Reset form when sidebar closes
  React.useEffect(() => {
    if (!isOpen) {
      setSelectedFiles([]);
      setDocumentName('');
      setEnableEmbedding(true);
      setExtractEntities(false);
      setSelectedEntityTypes(defaultEntityTypes || ['URS']);
      setVersion('1.0');
      setIsLoading(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  }, [isOpen, defaultEntityTypes]);

  // Update document name when single file is selected
  React.useEffect(() => {
    if (selectedFiles.length === 1 && !documentName) {
      const nameWithoutExt = selectedFiles[0].name.replace(/\.[^/.]+$/, '');
      setDocumentName(nameWithoutExt);
    }
  }, [selectedFiles, documentName]);

  const handleFileSelect = () => {
    fileInputRef.current?.click();
  };

  const allowedTypes = ['application/pdf', 'text/csv', 'image/jpeg', 'image/png', 'application/msword', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'];
  const maxSize = 24 * 1024 * 1024;

  const handleFileInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (!files?.length) return;

    const fileList = Array.from(files);
    const invalid = fileList.find((f) => !allowedTypes.includes(f.type));
    if (invalid) {
      showToast(t('project_tab_invalid_file_type') || 'Invalid file type', 'error');
      return;
    }
    const tooLarge = fileList.find((f) => f.size > maxSize);
    if (tooLarge) {
      showToast(t('project_tab_file_too_large') || 'File size must be less than 24MB', 'error');
      return;
    }

    setSelectedFiles(fileList);
  };

  const handleRemoveFile = (index: number) => {
    const next = selectedFiles.filter((_, i) => i !== index);
    setSelectedFiles(next);
    if (next.length === 0) setDocumentName('');
  };

  // Update entity types when extractEntities is toggled
  React.useEffect(() => {
    if (!extractEntities) {
      setSelectedEntityTypes([]);
    } else if (selectedEntityTypes.length === 0) {
      setSelectedEntityTypes(defaultEntityTypes || ['URS']);
    }
  }, [extractEntities, defaultEntityTypes, selectedEntityTypes.length]);

  // Update selectedEntityTypes when defaultEntityTypes changes (e.g., when sidebar opens)
  React.useEffect(() => {
    if (isOpen && defaultEntityTypes && defaultEntityTypes.length > 0) {
      setExtractEntities(true);
      setSelectedEntityTypes(defaultEntityTypes);
    }
  }, [isOpen, defaultEntityTypes]);

  const handleConfirm = async () => {
    if (!selectedFiles.length) {
      showToast(t('add_document_no_file_selected') || 'Please select a file', 'error');
      return;
    }

    if (selectedFiles.length === 1 && !documentName.trim()) {
      showToast(t('add_document_no_name') || 'Please enter a document name', 'error');
      return;
    }

    if (extractEntities && selectedEntityTypes.length === 0) {
      showToast(t('add_document_at_least_one_entity_type') || 'Please select at least one entity type', 'error');
      return;
    }

    if (!user) {
      showToast(t('add_document_not_authenticated') || 'You must be logged in to upload documents', 'error');
      return;
    }

    setIsLoading(true);
    onClose();

    showToast(t('add_document_uploading') || 'Uploading document...', 'info');

    const batchUrl = process.env.NEXT_PUBLIC_ADD_ENTITIES_FROM_DOCUMENTS_URL ||
      (process.env.NEXT_PUBLIC_ADD_ENTITY_FROM_DOCUMENT_URL || 'https://addentityfromdocument-3l53kqimxq-ey.a.run.app').replace('addentityfromdocument', 'addentitiesfromdocuments');

    try {
      const storage = getStorage();
      const filesWithUrls: Array<{ fileUrl: string; filename: string }> = [];

      for (const file of selectedFiles) {
        const sanitizedFileName = file.name
          .replace(/[^a-zA-Z0-9.-]/g, '_')
          .replace(/\s+/g, '_')
          .substring(0, 100);
        const documentId = `${Date.now()}_${sanitizedFileName}`;
        const storagePath = extractEntities && selectedEntityTypes.length > 0
          ? `projects/${projectId}/${selectedEntityTypes[0].toLowerCase()}/${documentId}`
          : `projects/${projectId}/documents/${documentId}`;
        const storageRef = ref(storage, storagePath);
        await uploadBytes(storageRef, file);
        const downloadURL = await getDownloadURL(storageRef);
        filesWithUrls.push({ fileUrl: downloadURL, filename: file.name });
      }

      startBatch(filesWithUrls.length, 'documents');
      showToast(t('add_document_processing_description') || 'Processing document...', 'info');

      const requestBody: any = {
        files: filesWithUrls,
        projectId,
        userId: user.uid,
        language: 'en',
      };
      if (extractEntities && selectedEntityTypes.length > 0) {
        requestBody.entityTypes = selectedEntityTypes;
      } else {
        requestBody.entityTypes = [];
      }
      if (enableEmbedding) requestBody.enableEmbedding = true;
      if (version.trim()) {
        const versionNum = parseFloat(version);
        if (!isNaN(versionNum)) requestBody.version = versionNum;
      }

      const response = await fetch(batchUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(requestBody),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || `HTTP ${response.status}`);
      }

      const result = await response.json();

      if (result.success || result.processed > 0) {
        const succeeded = result.processed ?? result.results?.filter((r: { success: boolean }) => r.success).length ?? 0;
        const failed = result.failed ?? 0;
        if (failed > 0) {
          showToast(
            t('add_document_batch_partial') || `Processed ${succeeded} document(s), ${failed} failed.`,
            'warning'
          );
        } else {
          showToast(
            succeeded === 1
              ? (t('add_document_success') || 'Document added successfully')
              : (t('add_document_batch_success') || `${succeeded} documents added successfully`),
            'success'
          );
        }
      } else {
        throw new Error(result.message || result.error || 'Unknown error occurred');
      }

      setSelectedFiles([]);
      setDocumentName('');
      setEnableEmbedding(true);
      setExtractEntities(false);
      setSelectedEntityTypes(['URS']);
      setVersion('1.0');
      if (fileInputRef.current) fileInputRef.current.value = '';

      onSuccess?.();
    } catch (error: unknown) {
      const errorMessage = error instanceof Error ? error.message : String(error);
      console.error('Add document error:', error);
      showToast(t('add_document_error') || `Failed to add document: ${errorMessage}`, 'error');
    } finally {
      endBatch();
      setIsLoading(false);
    }
  };

  const formatFileSize = (bytes: number): string => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
  };

  return (
    <GlobalSidebar isOpen={isOpen} onClose={onClose}>
      <div className={styles.content}>
        {/* Header */}
        <div className={styles.header}>
          <img 
            src={isDarkMode ? "/assets/identity/logo-dark.svg" : "/assets/identity/logo.svg"} 
            alt="Thescon" 
            width={16} 
            height={16} 
          />
          <span>|</span>
          <h2>{t('add_document_title') || 'Add Document'}</h2>
        </div>

        {/* File Picker Section */}
        <div className={styles.section}>
          <h3 className={styles.sectionTitle}>
            {t('add_document_select_file') || 'Select File'}
          </h3>
          <p className={styles.sectionDescription}>
            {t('add_document_select_file_description') || 'Choose a document to upload to this project'}
          </p>
          
          {selectedFiles.length === 0 ? (
            <div style={{ display: 'inline-block' }}>
              <GlobalButton
                onClick={handleFileSelect}
                variant="secondary"
                disabled={isLoading}
                autoWidth
                icon={<img src={isDarkMode ? "/assets/icons/icloud.and.arrow.up.svg" : "/assets/icons/icloud.and.arrow.up.black.svg"} alt="Upload" width={16} height={16} />}
              >
                {t('add_document_choose_files') || 'Choose File(s)'}
              </GlobalButton>
            </div>
          ) : (
            <div className={styles.fileList}>
              {selectedFiles.map((file, index) => {
                const fileIcon = fileIconFromFileName(file.name, isDarkMode);
                return (
                <div key={`${file.name}-${index}`} className={styles.filePreview}>
                  <div className={styles.fileInfo}>
                    <div className={styles.fileIcon}>
                      <img
                        src={fileIcon.src}
                        alt={fileIcon.alt}
                        width={24}
                        height={24}
                      />
                    </div>
                    <div className={styles.fileDetails}>
                      <div className={styles.fileName}>{file.name}</div>
                      <div className={styles.fileSize}>{formatFileSize(file.size)}</div>
                    </div>
                  </div>
                  <GlobalButton
                    onClick={() => handleRemoveFile(index)}
                    variant="secondary"
                    disabled={isLoading}
                    destructive
                    icon={<img src="/assets/icons/trash.red.svg" alt="Remove" width={12} height={12} />}
                  >
                    {t('add_document_remove') || 'Remove'}
                  </GlobalButton>
                </div>
                );
              })}
            </div>
          )}

          {/* Hidden file input */}
          <input
            ref={fileInputRef}
            type="file"
            accept=".pdf,.csv,.doc,.docx,image/jpeg,image/png"
            multiple
            onChange={handleFileInputChange}
            style={{ display: 'none' }}
            disabled={isLoading}
          />
        </div>

        {/* Document Name Section (single file only) */}
        {selectedFiles.length === 1 && (
          <div className={styles.section}>
            <GlobalInput
              label={t('add_document_name') || 'Document name'}
              value={documentName}
              onChange={(e) => setDocumentName(e.target.value)}
              disabled={isLoading}
              placeholder={t('add_document_name_placeholder') || 'Enter document name'}
            />
          </div>
        )}

        {/* Version Number */}
        {selectedFiles.length > 0 && (
          <div className={styles.section}>
            <GlobalInput
              label={t('add_document_version') || 'Version Number (optional)'}
              type="number"
              value={version}
              onChange={(e) => setVersion(e.target.value)}
              disabled={isLoading}
              placeholder={t('add_document_version_placeholder') || 'e.g., 1.0'}
            />
          </div>
        )}

        {/* Embedding Option */}
        {selectedFiles.length > 0 && (
          <div className={styles.section}>
            <div className={styles.checkboxGroup}>
              <GlobalCheckbox
                checked={enableEmbedding}
                onChange={(e) => setEnableEmbedding(e.target.checked)}
                disabled={isLoading}
                label={t('add_document_deep_memory') || 'Add to project\'s deep memory'}
              />
            </div>
          </div>
        )}

        {/* Entity Extraction Option */}
        {selectedFiles.length > 0 && (
          <div className={styles.section}>
            <div className={styles.checkboxGroup}>
              <GlobalCheckbox
                checked={extractEntities}
                onChange={(e) => setExtractEntities(e.target.checked)}
                disabled={isLoading}
                label={t('add_document_extract_entities') || 'Extract entities from document'}
              />
            </div>
            {extractEntities && (
              <div style={{ marginTop: '12px' }}>
                <p className={styles.sectionDescription} style={{ marginBottom: '8px', fontSize: '0.9rem' }}>
                  {t('add_document_select_entity_types') || 'Select entity types to extract:'}
                </p>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                  {availableEntityTypes.map((type) => (
                    <GlobalCheckbox
                      key={type}
                      checked={selectedEntityTypes.includes(type)}
                      onChange={() => handleEntityTypeToggle(type)}
                      disabled={isLoading}
                      label={getEntityTypeName(type)}
                    />
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {/* Confirm Button */}
        <div className={styles.actionSection}>
          <GlobalButton
            onClick={handleConfirm}
            disabled={isLoading || selectedFiles.length === 0 || (selectedFiles.length === 1 && !documentName.trim())}
            isLoading={isLoading}
          >
            {isLoading 
              ? (t('add_document_uploading') || 'Uploading...')
              : (t('add_document_confirm') || 'Add Document')}
          </GlobalButton>
        </div>

        <LocalLLMTrust />
      </div>
    </GlobalSidebar>
  );
}
